






































































import { computePosition, flip, offset, shift } from '@floating-ui/vue';
import type { PropType } from '@nuxtjs/composition-api';
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  useContext
} from '@nuxtjs/composition-api';
import debounce from 'lodash.debounce';

import {
  useMagentoConfiguration,
  useProduct,
  useUiNotification,
  useUiState
} from '~/composables';
import {
  useAlternativeVisual,
  useRequestPath
} from '~/diptyqueTheme/composable';
import { useDeclination } from '~/diptyqueTheme/composable/useDeclination';
import { useScreenSize } from '~/diptyqueTheme/composable/useScreenSize';
import {
  getFormattedLocale,
  getFormattingOptions
} from '~/diptyqueTheme/helpers/priceFormatter';
import {
  getOnlyProductCardPrice,
  getProductReplenishable
} from '~/diptyqueTheme/helpers/productGetters';
import type {
  ShoppableImageModel,
  ShoppableImagePin,
  ShoppableImagePinWithPosition
} from '~/diptyqueTheme/types/contentful';
import type { Visual } from '~/diptyqueTheme/types/contentful';
import { useAddToCart } from '~/helpers/cart/addToCart';
import formatCurrency from '~/helpers/formatCurrency';
import { Logger } from '~/helpers/logger';
import type { Product } from '~/modules/catalog/product/types';
import { ProductStockStatus } from '~/modules/GraphQL/types';

import InstalikeContent from './InstalikeContent.vue';
import InstalikePin from './InstalikePin.vue';
import InstalikeTooltip from './InstalikeTooltip.vue';
import type { TooltipProductI } from './types';
import { HEADING_LOCATIONS, TOOLTIP } from './types';

type ShoppableImagePinStyles = {
  left: string;
  top: string;
};

export default defineComponent({
  name: 'Instalike',
  components: {
    InstalikeContent,
    InstalikeTooltip,
    InstalikePin,
    VaimoBanner: () => import('atoms/VaimoBanner.vue')
  },
  props: {
    sectionData: {
      required: true,
      type: Object as PropType<ShoppableImageModel>
    },
    pageName: {
      type: String,
      required: false,
      default: ''
    }
  },
  setup(props) {
    const { app } = useContext();
    const { $gtm } = app;
    const { isDesktop } = useScreenSize();
    const { openNotifyMeModal } = useUiState();
    const { addItemToCart, loading: addProductToCartLoading } = useAddToCart();
    const { getBannerData: bannerData } = useDeclination(props.sectionData);
    const contentElem = ref<HTMLDivElement | null>(null);
    const { send: sendNotification } = useUiNotification();
    const { getProductList } = useProduct();
    const { applyAltVisuals } = useAlternativeVisual();
    const productList = ref([]);
    const sidebarIsOpen = ref(false);
    const { getRelatedProductUrl } = useRequestPath();
    const { selectedCurrency, selectedLocale } = useMagentoConfiguration();

    const tooltipRef = ref<HTMLDivElement | null>(null);
    const tooltipRefStyle = ref({});
    const tooltipProduct = ref<TooltipProductI | null>(null);
    const tooltipActive = ref(false);

    const isTooltipActive = (pin: ShoppableImagePinWithPosition) => {
      return tooltipActive.value && tooltipProduct.value?.sku === pin.sku;
    };

    const hasFullscreenVisual = computed<boolean>(() => {
      return props.sectionData?.visualFullscreen || false;
    });

    const headingLocation = computed(() => {
      return props.sectionData?.headingLocation || HEADING_LOCATIONS.TOP;
    });

    const hasLocationTop = computed<boolean>(() => {
      return headingLocation.value === HEADING_LOCATIONS.TOP;
    });

    const hasLocationBottom = computed<boolean>(() => {
      return headingLocation.value === HEADING_LOCATIONS.BOTTOM;
    });

    const originalVisual = computed(() => {
      return isDesktop.value
        ? props.sectionData?.visualDesktop
        : props.sectionData?.visual;
    });

    const altVisuals = computed(
      () => props.sectionData?.altvisCollection?.items ?? []
    );

    const pins = computed<ShoppableImagePinWithPosition[]>(() => {
      updateContentElementSizes();
      return props.sectionData?.pinsCollection?.items.reduce((memo, cur) => {
        if (cur) {
          const position = calculatePinPosition(cur);
          if (position) memo.push(position);
          return memo;
        }
      }, []);
    });

    const filteredPins = computed<ShoppableImagePinWithPosition[]>(() => {
      const skusList = productList.value.map((product) => product.sku);
      return pins.value.filter((pin) => skusList.includes(pin.sku));
    });

    const calculatePinPosition = (
      pin: ShoppableImagePin
    ): ShoppableImagePinWithPosition | null => {
      const focalPoint = isDesktop.value
        ? pin.pinDesktop.focalPoint
        : pin.pinMobile.focalPoint;

      if (!focalPoint) {
        return null;
      }

      const width = originalVisual.value.width;
      const height = originalVisual.value.height;
      /* eslint-disable no-magic-numbers */
      const yDoubleShift = width > height ? (1 - 9 / 16) * 100 * 2 : 0;
      const yShift = yDoubleShift / 2;
      const xDoubleShift = height > width ? (1 - 3 / 4) * 100 : 0;
      const xShift = xDoubleShift / 2;

      const left = Math.round(
        ((100 + xDoubleShift) / width) * focalPoint.x - xShift
      );
      const top = Math.round(
        ((100 + yDoubleShift) / height) * focalPoint.y - yShift
      );
      /* eslint-enable */

      return {
        ...pin,
        position: {
          left,
          top
        }
      };
    };

    const skuList = computed<string[]>(() => pins.value.map((pin) => pin.sku));

    const getProductDataForTooltip = (sku: string): TooltipProductI | null => {
      const product = productList.value.find((pr) => pr.sku === sku);

      if (!product) return null;

      const {
        name,
        plp_description,
        thumbnail: { url }
      } = product;

      const locale = getFormattedLocale(selectedLocale.value);
      const basePrice: string = getOnlyProductCardPrice(product);

      const priceFormated = formatCurrency(basePrice, locale, {
        style: 'currency',
        currency: selectedCurrency.value,
        ...getFormattingOptions(Number(basePrice))
      });

      let displayPrice = priceFormated;
      if (product?.price_type === 'DYNAMIC') {
        displayPrice = app.i18n
          .t('From {price}', {
            price: priceFormated
          })
          .toString();
      }

      return {
        id: product.id,
        sku,
        name,
        plp_description,
        price: displayPrice,
        thumbnailUrl: url,
        url: app.localePath(getRelatedProductUrl(product)),
        hasStock: product.stock_status === ProductStockStatus.InStock,
        replenishable: getProductReplenishable(product)
      };
    };

    const onPinClick = async (
      { sku }: ShoppableImagePinWithPosition,
      $event
    ) => {
      // Close tooltip if user clicks on the same pin of the current product
      if (tooltipProduct.value?.sku === sku) {
        cleanupTooltip();
        return;
      }

      getClickPictureDetails();
      const tooltipProductData = getProductDataForTooltip(sku);

      if (!tooltipProductData) {
        Logger.error('[Instalike]', 'No product data found for sku: ' + sku);
        return;
      }

      tooltipActive.value = true;
      tooltipProduct.value = tooltipProductData;

      const middleware = [
        ...(isDesktop.value
          ? [
              offset(TOOLTIP.OFFSET),
              flip({
                boundary: contentElem.value,
                fallbackPlacements: [TOOLTIP.PLACEMENTS.LEFT]
              }),
              shift({ boundary: contentElem.value })
            ]
          : [])
      ];

      // Wait for the next DOM update to ensure tooltipRef is available
      await nextTick();

      if ($event.target && tooltipRef.value) {
        const { x, y } = await computePosition(
          $event.target,
          tooltipRef.value,
          {
            placement: TOOLTIP.PLACEMENTS.RIGHT,
            middleware
          }
        );

        // Set tooltip position based on the computed x and y values
        tooltipRefStyle.value = isDesktop.value
          ? {
              left: `${x}px`,
              top: `${y}px`
            }
          : {
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%) !important'
            };
      }
    };

    const cleanupTooltip = () => {
      tooltipActive.value = false;
      tooltipProduct.value = null;
    };

    const contentElementSizes = reactive({
      width: 0,
      height: 0
    });

    function observeContentElHeight() {
      const resizeObserver = new ResizeObserver(function () {
        updateContentElementSizes();
      });
      resizeObserver.observe(contentElem.value);
    }

    const updateContentElementSizes = () => {
      if (!contentElem.value) return;
      contentElementSizes.width = contentElem.value?.offsetWidth;
      contentElementSizes.height = contentElem.value?.offsetHeight;
    };

    const getProductsBySku = async () => {
      const skusList = props.sectionData?.pinsCollection?.items.map(
        (pin) => pin.sku
      );

      try {
        const data = await getProductList({
          filter: {
            sku: {
              in: skusList
            }
          }
        });

        if (data?.items) {
          productList.value = data?.items.sort((item1, item2) => {
            const index1 = skusList.indexOf(item1.sku);
            const index2 = skusList.indexOf(item2.sku);
            return index1 - index2;
          });
        }
      } catch (e) {
        Logger.error(e);
      }
    };

    const applyAlternativeVisuals = () => {
      if (productList.value.length && altVisuals.value.length) {
        productList.value = applyAltVisuals(
          productList.value as Product[],
          altVisuals.value as Visual[]
        );
      }
    };

    const RESIZE_DEBOUNCE_DELAY = 300;
    const debouncedResize = debounce(cleanupTooltip, RESIZE_DEBOUNCE_DELAY);

    onMounted(async () => {
      observeContentElHeight();
      await getProductsBySku();
      applyAlternativeVisuals();

      window.addEventListener('resize', debouncedResize);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', debouncedResize);
    });

    const instalikeIdClass = computed(() => {
      return 'instalike--' + props.sectionData?.sys?.id;
    });

    const getPinStyles = (
      pin: ShoppableImagePinWithPosition
    ): ShoppableImagePinStyles => {
      return {
        top: pin.position.top + '%',
        left: pin.position.left + '%'
      };
    };

    const addProductToCart = async () => {
      try {
        const sku = tooltipProduct.value?.sku || '';
        const product = productList.value.find((item) => item.sku === sku);
        await addItemToCart({ product, quantity: 1 });
      } catch (error) {
        sendNotification({
          id: Symbol('instalike_add_error'),
          message: String(app.i18n.t('This product is currently unavailable')),
          persist: false,
          type: 'danger',
          area: 'top'
        });
        await app.router.push({ name: 'error' });
      }
    };

    // Toggle Notify Me modal
    const notifyMe = () => {
      if (!tooltipProduct.value?.id) return;
      openNotifyMeModal(tooltipProduct.value.id);
    };

    const getClickPictureDetails = () => {
      if (props?.pageName === 'Home') {
        $gtm.push({
          event: 'clickHPPictures',
          sectionName: props.sectionData?.heading || 'Instalike'
        });
      }
    };

    return {
      isDesktop,
      sidebarIsOpen,
      bannerData,
      productList,
      filteredPins,
      skuList,
      pins,
      contentElem,
      instalikeIdClass,
      onPinClick,
      getPinStyles,
      cleanupTooltip,
      notifyMe,
      openNotifyMeModal,
      addProductToCart,
      addProductToCartLoading,
      getClickPictureDetails,
      tooltipRef,
      tooltipRefStyle,
      tooltipActive,
      tooltipProduct,
      isTooltipActive,
      getOnlyProductCardPrice,
      hasFullscreenVisual,
      headingLocation,
      hasLocationTop,
      hasLocationBottom
    };
  }
});
